'use client';

import React, { useRef } from 'react';

import { Button } from '@/shared/components/ui/common/Button';
import {
  FeatherDownload,
  FeatherFileText,
  FeatherInfo,
  FeatherSettings,
  FeatherUpload,
} from '@subframe/core';

interface UploadPlanilhaOpcoesProps {
  onDownloadModelo: () => void;
  onSelectFile: (file: File, usarMapeamento: boolean) => void;
  disableDownload?: boolean;
  mostrarAvisoMultiplosContatos?: boolean;
}

export function UploadPlanilhaOpcoes({
  onDownloadModelo,
  onSelectFile,
  disableDownload,
  mostrarAvisoMultiplosContatos = false,
}: UploadPlanilhaOpcoesProps) {
  const fileInputModeloRef = useRef<HTMLInputElement>(null);
  const fileInputCustomRef = useRef<HTMLInputElement>(null);

  const handleFileChangeModelo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onSelectFile(file, false);
    }
    event.target.value = '';
  };

  const handleFileChangeCustom = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onSelectFile(file, true);
    }
    event.target.value = '';
  };

  const handleSelectModeloClick = () => {
    fileInputModeloRef.current?.click();
  };

  const handleSelectCustomClick = () => {
    fileInputCustomRef.current?.click();
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-[16px] border border-solid border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex w-full items-center gap-3 border-b border-solid border-neutral-100 pb-4">
        <div className="bg-brand-50 flex h-10 w-10 items-center justify-center rounded-[12px]">
          <FeatherFileText className="text-brand-600 h-5 w-5" />
        </div>
        <div className="flex flex-col">
          <h3 className="text-heading-3 font-heading-3 text-default-font">
            Importar Destinatários
          </h3>
          <p className="text-caption text-subtext-color">
            Escolha como deseja enviar os dados para este lote
          </p>
        </div>
      </div>

      {mostrarAvisoMultiplosContatos && (
        <div className="flex w-full items-start gap-2.5 rounded-[8px] border border-solid border-blue-300 bg-blue-100 px-4 py-3">
          <FeatherInfo className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-700" />
          <div className="flex flex-col gap-1">
            <p className="text-caption text-blue-900">
              <span className="font-semibold">Múltiplos contatos por destinatário: </span>
              informe quantos <strong>e-mails</strong> e <strong>telefones</strong> quiser por
              linha, separados por ponto e vírgula ( ; ).
            </p>
            <span className="text-caption font-medium text-blue-700">
              Ex: email1@empresa.com; email2@empresa.com
            </span>
          </div>
        </div>
      )}

      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2">
        <div className="border-brand-100 bg-brand-50/10 hover:bg-brand-50/20 relative flex flex-col items-start gap-4 rounded-[12px] border-2 border-solid p-5 transition-all">
          <div className="bg-brand-600 absolute -top-3 right-4 rounded-full px-3 py-1 shadow-sm">
            <span className="text-[10px] font-bold tracking-wider text-white uppercase">
              Recomendado
            </span>
          </div>

          <div className="flex items-center gap-2">
            <div className="border-brand-100 flex h-8 w-8 items-center justify-center rounded-lg border bg-white shadow-sm">
              <FeatherDownload className="text-brand-600 h-4 w-4" />
            </div>
            <span className="text-body-bold font-body-bold text-default-font">
              Usar Modelo Arbitralis
            </span>
          </div>
          <p className="text-sm leading-relaxed text-neutral-600">
            Baixe nossa planilha pré-configurada. Basta preencher os dados e fazer o upload.
            <strong> É o método mais rápido e seguro contra erros.</strong>
          </p>
          <div className="mt-auto flex w-full flex-col gap-3">
            <Button
              className="h-10 w-full"
              variant="brand-primary"
              icon={<FeatherDownload />}
              onClick={onDownloadModelo}
              disabled={disableDownload}
            >
              1. Baixar Planilha Modelo
            </Button>
            <input
              ref={fileInputModeloRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileChangeModelo}
            />
            <Button
              className="h-10 w-full"
              variant="neutral-secondary"
              icon={<FeatherUpload />}
              onClick={handleSelectModeloClick}
            >
              2. Upload da Planilha Preenchida
            </Button>
          </div>
        </div>

        <div className="flex flex-col items-start gap-4 rounded-[12px] border border-solid border-neutral-200 bg-neutral-50/30 p-5 transition-all hover:bg-neutral-50/60">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 bg-white shadow-sm">
              <FeatherSettings className="h-4 w-4 text-neutral-600" />
            </div>
            <span className="text-body-bold font-body-bold text-default-font">
              Tenho minha própria planilha
            </span>
          </div>
          <p className="text-sm leading-relaxed text-neutral-600">
            Faça o upload do seu arquivo (CSV ou XLSX) e nós ajudaremos você a identificar quais
            colunas correspondem ao Nome, CPF, E-mail, etc.
          </p>
          <div className="mt-auto w-full">
            <input
              ref={fileInputCustomRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileChangeCustom}
            />
            <Button
              className="h-10 w-full"
              variant="neutral-secondary"
              icon={<FeatherUpload />}
              onClick={handleSelectCustomClick}
            >
              Selecionar e Mapear Campos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
